import FunnelScreen from './FunnelScreen';
import LocationScreen from './screens/LocationScreen';
import { Wrapper } from './styles';
import {
  FunnellingBackButton,
  FunnellingFooter,
} from 'components/Funnelling/styles';
import { useSelector } from 'react-redux';
import { selectRecentSearchLocation } from 'containers/AppContainer/selectors';
import {
  type FunnelState,
  type ScreenCommonProps,
  type UseFunnelNext,
  type UseFunnelNextCallback,
} from 'components/Funnelling/types';
import { useCallback, useState } from 'react';
import type { AdditionalData } from 'lib/sdks/jobs-management';
import RAQGuidance from './components/RAQGuidance';

type CompletedFunnelData = {
  categoryId: number | null;
  postcode: string;
  additionalData: AdditionalData;
  currentScreen?: number;
  value?: string;
};

type FunnellingProps = {
  onBackToPreviousScreen?: () => void;
  onSelect?: UseFunnelNextCallback;
  locationButtonText?: string;
  next: UseFunnelNext;
  previous: () => void;
  skipLocation?: boolean;
} & FunnelState &
  ScreenCommonProps<[funnelResult: CompletedFunnelData]>;

const Funnelling = ({
  onComplete,
  onBack,
  locationButtonText = 'Search',
  funnelConfig,
  showLocation,
  steps,
  lastCategoryId,
  onBackToPreviousScreen,
  next,
  previous,
  onSelect,
  skipLocation,
  isRequestAQuote,
}: FunnellingProps) => {
  const recentSearchLocation = useSelector(selectRecentSearchLocation);
  const [additionalData, setAdditionalData] = useState<AdditionalData>([]);

  const backToPreviousScreen = () => {
    if (onBackToPreviousScreen) {
      onBackToPreviousScreen();
    }

    previous();
  };

  const handleSelect = useCallback(
    (key: number, choice: any, screenConfig: any) => {
      const newAdditionalData: AdditionalData = [
        ...additionalData,
        {
          question: screenConfig.title,
          shortName: screenConfig.shortName,
          answer: choice.title,
          questionId: key,
          answerId: choice.id,
        },
      ];

      if (screenConfig.storeData) {
        setAdditionalData(newAdditionalData);
      }

      next(
        {
          value: choice.value,
          categoryId: choice.category?.categoryId,
        },
        (currentScreen, value, goTo) => {
          if (!goTo && skipLocation) {
            onComplete({
              categoryId: lastCategoryId,
              postcode: '',
              additionalData: newAdditionalData,
              currentScreen,
              value,
            });
            return;
          }

          if (onSelect) {
            onSelect(currentScreen, value, goTo);
          }
        },
      );
    },
    [additionalData, next, skipLocation, onSelect, onComplete, lastCategoryId],
  );

  if (!funnelConfig) {
    return null;
  }

  if (steps.length === 0) {
    return null;
  }

  if (lastCategoryId && showLocation && !skipLocation) {
    const handleSubmit = (location: string) => {
      onComplete({
        categoryId: lastCategoryId,
        postcode: location,
        additionalData,
      });
    };

    return (
      <LocationScreen
        buttonText={locationButtonText}
        value={funnelConfig.location || recentSearchLocation}
        onComplete={handleSubmit}
        onBack={backToPreviousScreen}
      />
    );
  }

  return (
    <Wrapper as="article">
      <FunnelScreen
        key={`step-${steps[steps.length - 1]}`}
        screenId={steps[steps.length - 1]}
        onSelect={handleSelect}
      />
      {isRequestAQuote && <RAQGuidance />}
      <FunnellingFooter>
        <FunnellingBackButton
          onClick={steps.length > 1 ? backToPreviousScreen : onBack}
        />
      </FunnellingFooter>
    </Wrapper>
  );
};

export default Funnelling;
